import { useEffect, useState, useMemo, useCallback } from "react";
import {
  FileText,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  Clock,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { staffPortalApi } from "../services/StaffPortal/staffportalApi";

const MetricCardsCM = () => {
  const { user } = useAuth();
  const [allLoansData, setAllLoansData] = useState({
    processing: [],
    approved: [],
    rejected: []
  });
  const [selectedPeriod, setSelectedPeriod] = useState("allTime");
  const [loading, setLoading] = useState(true);
  const staffId = user?.id;

  // Fetch loans by status
  const fetchLoansData = async () => {
    if (!staffId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [processingLoans, approvedLoans, rejectedLoans] = await Promise.all([
        staffPortalApi.getLeadsByStatusforCM("processing", staffId),
        staffPortalApi.getLeadsByStatusforCM("approved", staffId),
        staffPortalApi.getLeadsByStatusforCM("rejected", staffId)
      ]);

      setAllLoansData({
        processing: processingLoans || [],
        approved: approvedLoans || [],
        rejected: rejectedLoans || []
      });
    } catch (error) {
      console.error("Error fetching loans data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoansData();
  }, [staffId]);

  // Filter loans by period
  const filterLoansByPeriod = useCallback((loans, period) => {
    if (!loans) return [];
    const today = new Date();
    let startDate, endDate;

    switch (period) {
      case "thisMonth":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0,
          23,
          59,
          59,
          999
        );
        break;
      case "quarterly":
        startDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        endDate = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0,
          23,
          59,
          59,
          999
        );
        break;
      case "thisYear":
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today.getFullYear(), 11, 31, 23, 59, 59, 999);
        break;
      case "allTime":
      default:
        return loans;
    }
    return loans.filter((loan) => {
      const loanDate = new Date(loan.createdAt);
      return (
        !isNaN(loanDate.getTime()) &&
        loanDate >= startDate &&
        loanDate <= endDate
      );
    });
  }, []);

  // Calculate metrics based on filtered data
  const { processingLoans, approvedLoans, rejectedLoans, totalLoans, totalLoanAmount } = useMemo(() => {
    const filteredProcessing = filterLoansByPeriod(allLoansData.processing, selectedPeriod);
    const filteredApproved = filterLoansByPeriod(allLoansData.approved, selectedPeriod);
    const filteredRejected = filterLoansByPeriod(allLoansData.rejected, selectedPeriod);
    
    const allFilteredLoans = [...filteredProcessing, ...filteredApproved, ...filteredRejected];
    const totalAmount = allFilteredLoans.reduce(
      (sum, loan) => sum + (parseFloat(loan.principle) || 0),
      0
    );
    
    return {
      processingLoans: filteredProcessing.length,
      approvedLoans: filteredApproved.length,
      rejectedLoans: filteredRejected.length,
      totalLoans: allFilteredLoans.length,
      totalLoanAmount: totalAmount
    };
  }, [allLoansData, selectedPeriod, filterLoansByPeriod]);

  // Metrics array
  const metrics = useMemo(
    () => [
      {
        title: "TOTAL LOANS",
        count: totalLoans.toLocaleString("en-IN"),
        color: "bg-yellow-500",
        icon: <FileText className="w-4 h-4" />,
      },
      {
        title: "PROCESSING LOANS",
        count: processingLoans.toLocaleString("en-IN"),
        color: "bg-yellow-500",
        icon: <AlertTriangle className="w-4 h-4" />,
      },
      {
        title: "APPROVED LOANS",
        count: approvedLoans.toLocaleString("en-IN"),
        color: "bg-yellow-500",
        icon: <CheckCircle className="w-4 h-4" />,
      },
      {
        title: "REJECTED LOANS",
        count: rejectedLoans.toLocaleString("en-IN"),
        color: "bg-yellow-500",
        icon: <CheckCircle className="w-4 h-4" />,
      },
      {
        title: "TOTAL LOAN AMOUNT",
        count: `₹${totalLoanAmount.toLocaleString("en-IN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}`,
        color: "bg-yellow-500",
        icon: <FileText className="w-4 h-4" />,
      },
    ],
    [totalLoans, processingLoans, approvedLoans, rejectedLoans, totalLoanAmount]
  );

  // Period options
  const periodOptions = [
    { value: "allTime", label: "All Time" },
    { value: "thisYear", label: "Yearly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "thisMonth", label: "Monthly" },
  ];

  // Handler for period change
  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Filter Dropdown */}
      <div className="mb-4 flex justify-end">
        <div className="relative">
          <select
            value={selectedPeriod}
            onChange={handlePeriodChange}
            className="bg-[#131313] text-sm px-3 py-2 rounded border border-[#333333] focus:border-yellow-500 focus:outline-none appearance-none pr-8"
          >
            {periodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={16}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none"
          />
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {metrics.map((metric, index) => (
          <div
            key={index}
            className="bg-[#131313] rounded-lg p-4 border border-[#333333]"
          >
            <div className="flex justify-between mb-1">
              <h3 className="text-xs text-gray-400">{metric.title}</h3>
              <div
                className={`${metric.color} rounded-full p-2 h-8 w-8 flex items-center justify-center`}
              >
                {metric.icon}
              </div>
            </div>
            <p className="text-xl font-semibold">{metric.count}</p>
            <p className="text-xs mt-1 text-gray-500">
              {periodOptions
                .find((opt) => opt.value === selectedPeriod)
                ?.label.toLowerCase() || "filter"}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MetricCardsCM;